import _ from "lodash";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Search from "../../components/form/Search";
import Pagination from "../../components/Pagination";
import SortableHeader from "../../components/SortableHeader";
import useResponsiveLimit from "../../composables/useResponsiveLimit";
import { Role, useAuthStore } from "../../stores/auth";
import { useDeliveryNoteTemplatesStore } from "../../stores/delivery_note_templates";
import { useDeliveryNotesStore } from "../../stores/delivery_notes";
import {
  buildRestoredListQuery,
  LIST_SECTION,
  useListPreferencesStore,
} from "../../stores/list_preferences";
import { formatDate } from "../../utils/dateFormat";
import { countFileTypes } from "../../utils/files";
import MobileListActionBar from "../../components/mobile-action-bar/MobileListActionBar";
import { formatAmount } from "./utils";

const FILTER = {
  ALL: "all",
  DRAFT: "draft",
  DUE: "due",
  REVIEW: "review",
  BILL: "bill",
  BILL_OR_REVIEW: "bill_or_review",
  BILLED: "billed",
  CANCELED: "canceled",
};

const DEFAULT_SORT_BY = "number";
const DEFAULT_SORT_DIR = "desc";

const FIELD_TAB_ORDER = [
  FILTER.DRAFT,
  FILTER.BILL_OR_REVIEW,
  FILTER.BILLED,
  FILTER.ALL,
];
const BACKOFFICE_TAB_ORDER = [
  FILTER.DRAFT,
  FILTER.REVIEW,
  FILTER.BILL,
  FILTER.BILLED,
  FILTER.CANCELED,
  FILTER.ALL,
];

function DeliveryNote({ delivery_note, showInvoiceNumber, showStatus, currentUserId }) {
  const { t } = useTranslation();
  const fileCounts = countFileTypes(delivery_note.files);

  const badgeColor = "success";
  //switch (delivery_note.state) {
  //  case "pending":
  //    badgeColor = "warning";
  //    break;
  //  case "delivered":
  //    badgeColor = "lime";
  //    break;
  //  case "completed":
  //    badgeColor = "green";
  //    break;
  //}

  return (
    <tr>
      <td>
        <Link to={`${delivery_note.id}/details`} className="fw-bold">
          {delivery_note.number}
        </Link>
        {delivery_note.coworkers?.length > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("delivery_notes.coworkers")}>
            <i className="fa fa-users fa-fw me-1"></i>
            {delivery_note.coworkers.length + (delivery_note.performed_by ? 1 : 0)}
          </span>
        )}
        {delivery_note.comments?.length > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("menu.comments")}>
            <i className="fa fa-comment fa-fw"></i>
            {delivery_note.comments.length}
          </span>
        )}
        {!!delivery_note.customer_signature && (
          <span className="badge bg-default text-dark ms-1" title={t("common.customer_signed")}>
            <i className="fa fa-signature fa-fw"></i>
          </span>
        )}
        {fileCounts.images > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("files.images")}>
            <i className="fa fa-image fa-fw me-1"></i>
            {fileCounts.images}
          </span>
        )}
        {fileCounts.pdfs > 0 && (
          <span className="badge bg-default text-dark ms-1" title={t("files.pdfs")}>
            <i className="fa fa-file-pdf fa-fw me-1"></i>
            {fileCounts.pdfs}
          </span>
        )}
        {delivery_note.work_order && (
          <Link
            to={`/work_orders/${delivery_note.work_order.id}/details`}
            className="badge badge-link bg-default text-dark ms-1"
            title={`${t("work_orders.work_order")} ${delivery_note.work_order.number}`}
          >
            <i className="fa fa-clipboard-list fa-fw"></i>
          </Link>
        )}
      </td>
      <td>{formatDate(delivery_note.delivered_at)}</td>
      {showInvoiceNumber && <td>{delivery_note.invoice_number}</td>}
      <td>
        <ul className="list-group list-unstyled">
          {!_.isEmpty(delivery_note.customer.number) && <li>{delivery_note.customer.number}</li>}
          {!_.isEmpty(delivery_note.customer.name) && (
            <li className="text-truncate" style={{ maxWidth: "25ch" }} title={delivery_note.customer.name}>
              {delivery_note.customer.name}
            </li>
          )}
        </ul>
      </td>
      <td>
        <ul className="list-group list-unstyled">
          {!_.isEmpty(delivery_note.customer_property.name?.trim()) && (
            <li
              className="text-truncate"
              style={{ maxWidth: "25ch" }}
              title={delivery_note.customer_property.name}
            >
              {delivery_note.customer_property.name}
            </li>
          )}
          {delivery_note.customer_property.addresses.map((address) => {
            const nameMatchesStreet = delivery_note.customer_property.name?.trim() === address.street?.trim();
            return (
              <li key={address.id}>
                <ul className="list-group list-unstyled">
                  {!!address.street && !nameMatchesStreet && <li>{address.street}</li>}
                  {(!!address.zip || !!address.city) && (
                    <li>
                      {address.zip} {address.city}
                    </li>
                  )}
                </ul>
              </li>
            );
          })}
        </ul>
      </td>

      {showStatus && (
        <td>
          <ul className="list-group list-unstyled">
            {delivery_note.state_transitions.slice(-1).map((transition) => (
              <li key={transition.id}>
                <span
                  className={`badge border border-${badgeColor} text-${badgeColor} px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center`}
                >
                  <i className="fa fa-circle fs-9px fa-fw me-5px"></i> {t(`delivery_notes.states.${transition.state}`)}
                </span>
              </li>
            ))}
          </ul>
        </td>
      )}
      <td>
        <ul className="list-group list-unstyled">
          {delivery_note.items
            .filter((item) => item.origin?.highlight)
            .map((item) => (
              <li key={item.id}>
                <span>
                  {item.number}|{item.name}|{formatAmount(item.amount)}x
                </span>
              </li>
            ))}
          <li>
            {delivery_note.items.length} {t("items.items")}
            <br />
            {Math.round(delivery_note.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}{" "}
            {t("items.amount")}
          </li>
        </ul>
      </td>
      <td>
        <ul className="list-unstyled mb-0">
          {delivery_note.performed_by ? (
            <li>
              {delivery_note.performed_by.name}
              {delivery_note.performed_by.number && (
                <small className="text-muted"> ({delivery_note.performed_by.number})</small>
              )}
            </li>
          ) : (
            <li>
              <span className="text-muted">{t("delivery_notes.no_performer")}</span>
            </li>
          )}
          {delivery_note.coworkers?.map((cw) => (
            <li key={cw.id}>
              {cw.name}
              {cw.number && <small className="text-muted"> ({cw.number})</small>}
            </li>
          ))}
        </ul>
      </td>
      <td>
        {delivery_note.assigned_to ? (
          <>
            {delivery_note.assigned_to.name}
            {delivery_note.assigned_to.number && (
              <small className="text-muted"> ({delivery_note.assigned_to.number})</small>
            )}
          </>
        ) : (
          <span className="text-muted">{t("delivery_notes.unassigned")}</span>
        )}
      </td>
      <td>{delivery_note.template?.name}</td>
    </tr>
  );
}

function DeliveryNotes() {
  const { t } = useTranslation();
  const [urlQueryParams, setQueryhParams] = useSearchParams();
  const savedListPreferences = useListPreferencesStore(
    (state) => state.preferences[LIST_SECTION.DELIVERY_NOTES],
  );
  const rememberListPreferences = useListPreferencesStore(
    (state) => state.remember,
  );
  const hasUrlQuery = Boolean(urlQueryParams.toString());
  const restoredQueryString = buildRestoredListQuery(
    urlQueryParams,
    savedListPreferences,
  );
  const queryParams = restoredQueryString
    ? new URLSearchParams(restoredQueryString)
    : urlQueryParams;

  const me = useAuthStore((state) => state.me);
  const hasRole = useAuthStore((state) => state.hasRole);
  const canReview = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const canBill = hasRole(Role.ADMINISTRATOR) || hasRole(Role.MANAGER) || hasRole(Role.OFFICE);
  const tabOrder = canReview ? BACKOFFICE_TAB_ORDER : FIELD_TAB_ORDER;

  const {
    byIds: deliveryNotesByIds,
    allIds: deliveryNotesAllIds,
    total: deliveryNotesTotal,
    totalReviewable: deliveryNotesTotalReviewable,
    totalBillable: deliveryNotesTotalBillable,
    fetchSome: deliveryNotesFetchSome,
    fetchDueTotal: deliveryNotesFetchDueTotal,
    fetchReviewableTotal: deliveryNotesFetchReviewableTotal,
    fetchBillableTotal: deliveryNotesFetchBillableTotal,
  } = useDeliveryNotesStore(
    useShallow((state) => ({
      byIds: state.byIds,
      allIds: state.allIds,
      total: state.total,
      totalReviewable: state.totalReviewable,
      totalBillable: state.totalBillable,
      fetchSome: state.fetchSome,
      fetchDueTotal: state.fetchDueTotal,
      fetchReviewableTotal: state.fetchReviewableTotal,
      fetchBillableTotal: state.fetchBillableTotal,
    })),
  );

  const deliveryNotes = deliveryNotesAllIds.map((id) => deliveryNotesByIds[id]);

  const {
    selectedId: selectedTemplateId,
    byIds: templatesByIds,
    allIds: templatesAllIds,
    fetchSome: fetchSomeTemplates,
    setSelectedId: setSelectedTemplateId,
  } = useDeliveryNoteTemplatesStore(
    useShallow((state) => ({
      selectedId: state.selectedId,
      byIds: state.byIds,
      allIds: state.allIds,
      fetchSome: state.fetchSome,
      setSelectedId: state.setSelectedId,
    })),
  );

  const selectedTemplate = Number.isInteger(selectedTemplateId)
    ? templatesByIds[selectedTemplateId]
    : templatesByIds[templatesAllIds[0]];

  const search = queryParams.get("q") || "";
  const filterParam = queryParams.get("filter");
  const filter = tabOrder.includes(filterParam) ? filterParam : tabOrder[0];
  const showInvoiceNumber = filter === FILTER.BILLED || filter === FILTER.ALL;
  const showStatus = filter === FILTER.ALL;
  const defaultLimit = useResponsiveLimit(10);
  const limit = parseInt(queryParams.get("limit")) || defaultLimit;
  const page = parseInt(queryParams.get("page")) || 0;
  const offset = page * limit;
  const sortBy = queryParams.get("sort_by") || DEFAULT_SORT_BY;
  const sortDir = queryParams.get("sort_dir") || DEFAULT_SORT_DIR;

  const updateParams = (updates) => {
    const nextParams = new URLSearchParams(queryParams);
    nextParams.set("page", "0");
    Object.entries(updates).forEach(([key, value]) => {
      if (value) nextParams.set(key, value);
      else nextParams.delete(key);
    });
    setQueryhParams(nextParams);
  };

  const handleSort = (column) => {
    const newDir = sortBy === column && sortDir === "desc" ? "asc" : "desc";
    updateParams({ sort_by: column, sort_dir: newDir });
  };

  const handleResetFilters = () => {
    const defaultPreferences = {
      filter: tabOrder[0],
      sort_by: DEFAULT_SORT_BY,
      sort_dir: DEFAULT_SORT_DIR,
    };
    rememberListPreferences(LIST_SECTION.DELIVERY_NOTES, defaultPreferences);
    setQueryhParams(
      buildRestoredListQuery(new URLSearchParams(), defaultPreferences),
      { replace: true },
    );
  };

  useEffect(() => {
    if (restoredQueryString) {
      setQueryhParams(restoredQueryString, { replace: true });
    }
  }, [restoredQueryString, setQueryhParams]);

  useEffect(() => {
    if (!hasUrlQuery) return;

    rememberListPreferences(LIST_SECTION.DELIVERY_NOTES, {
      filter,
      sort_by: sortBy,
      sort_dir: sortDir,
    });
  }, [filter, hasUrlQuery, rememberListPreferences, sortBy, sortDir]);

  useEffect(() => {
    deliveryNotesFetchSome({
      q: search,
      filter,
      limit,
      offset,
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit, filter, sortBy, sortDir]);

  useEffect(() => {
    deliveryNotesFetchDueTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canBill) {
      deliveryNotesFetchBillableTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBill]);

  useEffect(() => {
    if (canReview) {
      deliveryNotesFetchReviewableTotal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview]);

  useEffect(() => {
    fetchSomeTemplates({ q: "", limit: 20, offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    updateParams({ q: e.target.value });
  };

  const allTabs = [
    { filter: FILTER.DRAFT, label: "menu.drafts" },
    { filter: FILTER.BILLED, label: "menu.billed" },
    {
      filter: FILTER.REVIEW,
      label: "menu.review",
      badge: deliveryNotesTotalReviewable,
      badgeColor: "bg-success",
    },
    {
      filter: FILTER.BILL,
      label: "menu.bill",
      badge: deliveryNotesTotalBillable,
      badgeColor: "bg-success",
    },
    { filter: FILTER.BILL_OR_REVIEW, label: "menu.bill_or_review" },
    { filter: FILTER.CANCELED, label: "menu.canceled" },
    { filter: FILTER.ALL, label: "menu.all" },
  ];

  const tabs = tabOrder.map((f) => allTabs.find((tab) => tab.filter === f)).filter(Boolean);

  return (
    <div>
      <Breadcrumb items={[{ label: t("delivery_notes.delivery_notes") }]}>
        {!!selectedTemplate && templatesAllIds.length > 1 && (
          <div className="btn-group d-none d-md-flex">
            <Link to={`add?templateId=${selectedTemplate.id}`} className="btn btn-red">
              {selectedTemplate.name}
            </Link>
            <button type="button" data-bs-toggle="dropdown" className="btn btn-red dropdown-toggle">
              <i className="fa fa-caret-down" />
            </button>
            <div className="dropdown-menu dropdown-menu-end">
              {templatesAllIds
                .filter((sid) => sid !== selectedTemplate.id)
                .map((sid) => templatesByIds[sid])
                .map((template) => (
                  <Link
                    key={template.id}
                    to={`add?templateId=${template.id}`}
                    onClick={() => setSelectedTemplateId({ id: template.id })}
                    className="dropdown-item"
                  >
                    {template.name}
                  </Link>
                ))}
            </div>
          </div>
        )}
        {!!selectedTemplate && templatesAllIds.length === 1 && (
          <Link to={`add?templateId=${selectedTemplate.id}`} className="btn btn-red d-none d-md-inline-block">
            {selectedTemplate.name}
          </Link>
        )}
      </Breadcrumb>

      <MobileListActionBar />

      {/*<div className="mb-3 mx-3 mx-md-0 d-sm-flex fw-bold">
        <div className="mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-download fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            {t("menu.export")}
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2">
          <a href="#/" className="text-dark text-decoration-none">
            <i className="fa fa-upload fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            {t("menu.import")}
          </a>
        </div>
        <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2 dropdown-toggle">
          <a
            href="#/"
            data-bs-toggle="dropdown"
            className="text-dark text-decoration-none"
          >
            <i className="fa fa-cog fa-fw me-1 text-dark text-opacity-50"></i>{" "}
            {t("menu.more")} <b className="caret"></b>
          </a>
          <div className="dropdown-menu">
            <a className="dropdown-item" href="#/">
              Action
            </a>
            <a className="dropdown-item" href="#/">
              Another action
            </a>
            <a className="dropdown-item" href="#/">
              Something else here
            </a>
            <div role="separator" className="dropdown-divider"></div>
            <a className="dropdown-item" href="#/">
              Separated link
            </a>
          </div>
        </div>
      </div>*/}

      <div className="card border-0">
        <div className="d-md-none px-3 pt-3">
          <select
            className="form-select"
            value={filter}
            onChange={(e) => updateParams({ filter: e.target.value })}
          >
            {tabs.map((tab) => (
              <option key={tab.filter} value={tab.filter}>
                {t(tab.label)}
              </option>
            ))}
          </select>
        </div>
        <ul className="nav nav-tabs nav-tabs-v2 px-3 d-none d-md-flex">
          {tabs.map((tab) => (
            <li key={tab.filter} className="nav-item me-2">
              <button
                onClick={() => updateParams({ filter: tab.filter })}
                className={`nav-link px-2 ${filter === tab.filter && "active"}`}
              >
                {t(tab.label)}{" "}
                {tab.badge > 0 && (
                  <span className={`badge ${tab.badgeColor} fs-12px rounded-pill ms-auto pt-4px pb-5px px-8px`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            <div className="row g-2 mb-3">
              <div className="col">
                <Search
                  className="input-group"
                  handleSearch={handleSearch}
                  search={search}
                />
              </div>
              <div className="col-auto ms-auto">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={handleResetFilters}
                  aria-label={t("common.reset_filters")}
                  title={t("common.reset_filters")}
                >
                  <i className="fa fa-rotate-left fa-fw" />
                </button>
              </div>
            </div>

            <div className="table-responsive mb-3">
              <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <SortableHeader column="number" label={t("delivery_notes.number")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader column="delivered_at" label={t("delivery_notes.date")} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    {showInvoiceNumber && <th>{t("invoices.number")}</th>}
                    <th>{t("customers.customer")}</th>
                    <th>{t("customers.properties.property")}</th>
                    {showStatus && <th>{t("delivery_notes.menu.status")}</th>}
                    <th>{t("items.items")}</th>
                    <th>{t("common.expert")}</th>
                    <th>{t("delivery_notes.assigned_to")}</th>
                    <th>{t("delivery_notes.templates.template")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryNotes.map((deliveryNote) => (
                    <DeliveryNote
                      key={deliveryNote.id}
                      delivery_note={deliveryNote}
                      showInvoiceNumber={showInvoiceNumber}
                      showStatus={showStatus}
                      currentUserId={me?.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              basePath="/delivery_notes"
              page={page}
              limit={limit}
              total={deliveryNotesTotal}
              queryParams={queryParams}
              label={t("delivery_notes.delivery_notes")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryNotes;
